<script setup lang="ts">
import { useField } from 'vee-validate'
import { computed, watch } from 'vue'

interface Option {
  label: string
  value: string | number
}

const emit = defineEmits<(e: 'update:modelValue', value: string | number) => void>()

const props = defineProps({
  name: {
    type: String,
    required: false,
    default: '',
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  options: {
    type: Array as () => Option[],
    default: () => [],
  },
  appearance: {
    type: String,
    default: '',
  },
})

const field = props.name
  ? useField<string | number>(props.name, undefined, {
      initialValue: props.modelValue,
    })
  : null

const value = computed({
  get: () => (field ? field.value.value : props.modelValue),
  set: (newValue) => {
    if (field) {
      field.value.value = newValue
    }

    emit('update:modelValue', newValue)
  },
})

const errorMessage = computed(() => field?.errorMessage.value || '')

const hasError = computed(() => !!errorMessage.value || !!props.error)

const selectOption = (option: Option) => {
  if (props.disabled) return

  value.value = option.value

  if (field) {
    field.handleBlur()
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (field && newValue !== field.value.value) {
      field.value.value = newValue
    }
  }
)
</script>

<template>
    <div class="relative flex flex-col gap-1 w-full">
        <!-- label -->
        <label v-if="label" class="w-fit bg-white px-1 transition-all duration-300 z-50 pointer-events-none" :class="[
            'absolute ms-4 -mt-2 text-[10px] font-bold',
            appearance === 'admin'
                ? 'text-[#6C6C6C]'
                : 'text-[#1b325a]',
        ]">
            {{ label }}
        </label>

        <!-- segmented control -->
        <div class="flex items-center w-full h-12 p-1 rounded-xl border bg-[#F6F6F6] transition" :class="[
            hasError
                ? 'border-[#ed145b]'
                : 'border-[#E0E0E0]',
            disabled
                ? 'bg-gray-100 cursor-not-allowed opacity-60'
                : '',
        ]">
            <button v-for="option in options" :key="option.value" type="button" :disabled="disabled"
                class="flex-1 h-full rounded-lg text-[13px] font-medium transition-all duration-200" :class="[
                    value === option.value
                        ? 'bg-white shadow-sm font-bold text-[#1A1A1A]'
                        : 'bg-transparent text-[#6C6C6C]',
                    !disabled
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed',
                ]" @click="selectOption(option)">
                {{ option.label }}
            </button>
        </div>

        <!-- error -->
        <ErrorMessage v-if="name" :name="name" />
    </div>
</template>